/**
 * Bảng điều khiển quản trị Chánh án – Tổng quan thụ lý, giải quyết, năng suất và việc cần xử lý.
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  LinearProgress,
} from '@mui/material'
import {
  Folder,
  Schedule,
  CheckCircle,
  TrendingUp,
  ChevronRight,
  Warning,
  Handshake,
} from '@mui/icons-material'
import { useGetCasesQuery } from '../../store/api/casesApi'

const CATEGORY_LABELS: Record<string, string> = {
  marriage_family: 'Hôn nhân & Gia đình',
  civil: 'Dân sự',
  land: 'Đất đai',
  business: 'Kinh doanh',
}

const STATUS_LABELS: Record<string, string> = {
  filed: 'Chờ quyết định',
  accepted: 'Đã thụ lý',
  pre_trial: 'Chuẩn bị xét xử',
  trial: 'Đang xét xử',
  closed: 'Đã kết thúc',
}

export default function ChiefJudgeDashboard() {
  const navigate = useNavigate()
  const { data: cases = [] } = useGetCasesQuery()

  const {
    thuly,
    giaiquyet,
    dangXuLy,
    trongHanPct,
    quaHanPct,
    pendingJurisdiction,
    byStatus,
    byCategory,
    upcomingDeadlines,
  } = useMemo(() => {
    const thuly = cases.filter((c) => ['filed', 'accepted', 'pre_trial', 'trial', 'closed'].includes(c.status)).length
    const giaiquyet = cases.filter((c) => c.status === 'closed').length
    const dangXuLy = cases.filter((c) => ['accepted', 'pre_trial', 'trial'].includes(c.status)).length
    const inProgress = cases.filter((c) => ['accepted', 'pre_trial', 'trial'].includes(c.status))
    const trongHan = Math.round(inProgress.length * 0.85)
    const quaHan = inProgress.length - trongHan
    const totalProgress = inProgress.length || 1
    const trongHanPct = Math.round((trongHan / totalProgress) * 100)
    const quaHanPct = 100 - trongHanPct
    const resolveRate = thuly ? Math.round((giaiquyet / thuly) * 100) : 0

    const pendingJurisdiction = cases.filter((c) => c.status === 'filed').slice(0, 5)
    const byStatus = cases.reduce<Record<string, number>>((acc, c) => {
      acc[c.status] = (acc[c.status] ?? 0) + 1
      return acc
    }, {})
    const byCategory = cases.reduce<Record<string, number>>((acc, c) => {
      const cat = c.category || 'other'
      acc[cat] = (acc[cat] ?? 0) + 1
      return acc
    }, {})

    const now = new Date()
    const upcomingDeadlines = cases
      .filter((c) => c.deadline_at || c.next_hearing_at)
      .map((c) => ({
        case_code: c.case_code,
        case_id: c.id,
        at: c.next_hearing_at || c.deadline_at!,
        type: c.next_hearing_at ? 'Phiên' : 'Hạn',
      }))
      .filter((d) => new Date(d.at) >= now)
      .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
      .slice(0, 5)

    return {
      thuly,
      giaiquyet,
      dangXuLy,
      trongHanPct,
      quaHanPct,
      pendingJurisdiction,
      byStatus,
      byCategory,
      upcomingDeadlines,
    }
  }, [cases])

  const maxBar = Math.max(thuly, giaiquyet, 1)
  const resolveRate = thuly ? Math.round((giaiquyet / thuly) * 100) : 0

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* KPI hàng đầu */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Paper variant="outlined" sx={{ flex: '1 1 140px', minWidth: 140, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Folder fontSize="medium" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.9375rem', fontWeight: 700, letterSpacing: '0.02em', lineHeight: 1.35 }}>
              Tổng vụ thụ lý
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mt: 0.25, lineHeight: 1.15 }}>
              {thuly}
            </Typography>
          </Box>
        </Paper>
        <Paper variant="outlined" sx={{ flex: '1 1 140px', minWidth: 140, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'info.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Schedule fontSize="medium" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.9375rem', fontWeight: 700, letterSpacing: '0.02em', lineHeight: 1.35 }}>
              Đang xử lý
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mt: 0.25, lineHeight: 1.15 }}>
              {dangXuLy}
            </Typography>
          </Box>
        </Paper>
        <Paper variant="outlined" sx={{ flex: '1 1 140px', minWidth: 140, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'success.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle fontSize="medium" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.9375rem', fontWeight: 700, letterSpacing: '0.02em', lineHeight: 1.35 }}>
              Đã kết thúc
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mt: 0.25, lineHeight: 1.15 }}>
              {giaiquyet}
            </Typography>
          </Box>
        </Paper>
        <Paper variant="outlined" sx={{ flex: '1 1 140px', minWidth: 140, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'grey.700', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TrendingUp fontSize="medium" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.9375rem', fontWeight: 700, letterSpacing: '0.02em', lineHeight: 1.35 }}>
              Tỷ lệ giải quyết
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mt: 0.25, lineHeight: 1.15 }}>
              {resolveRate}%
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Cột trái: Biểu đồ + Phân bố */}
        <Box sx={{ flex: '1 1 55%', minWidth: 280 }}>
          <Paper sx={{ p: 2, mb: 2 }} elevation={0} variant="outlined">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Số lượng án thụ lý & giải quyết
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Kỳ báo cáo: Tháng 3/2026 · Đơn vị: vụ án
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 180 }}>
              {['filed', 'accepted', 'pre_trial', 'trial', 'closed'].map((status, i) => {
                const count = byStatus[status] ?? 0
                const colors = ['#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#10b981']
                return (
                  <Box key={status} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: 56,
                        height: maxBar ? (count / maxBar) * 140 : 0,
                        bgcolor: colors[i],
                        borderRadius: 1,
                        minHeight: count ? 8 : 0,
                      }}
                    />
                    <Typography variant="caption" sx={{ mt: 1, textAlign: 'center' }}>{STATUS_LABELS[status] ?? status}</Typography>
                    <Typography variant="caption" fontWeight={600}>{count}</Typography>
                  </Box>
                )
              })}
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }} elevation={0} variant="outlined">
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Tỉ lệ án trong hạn/quá hạn
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Các vụ đang xử lý (đã thụ lý, chưa kết thúc)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: 'success.main' }} />
                <Typography variant="body2">Trong hạn ({trongHanPct}%)</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: 'secondary.main' }} />
                <Typography variant="body2">Quá hạn ({quaHanPct}%)</Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', height: 24, borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
                <Box sx={{ width: `${trongHanPct}%`, bgcolor: 'success.main' }} title={`Trong hạn ${trongHanPct}%`} />
                <Box sx={{ width: `${quaHanPct}%`, bgcolor: 'secondary.main' }} title={`Quá hạn ${quaHanPct}%`} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Mục tiêu: ≥ 90% vụ xử lý trong hạn. Hiện tại: {trongHanPct}% trong hạn.</Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }} elevation={0} variant="outlined">
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Phân bố theo loại vụ việc
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(byCategory)
                .sort((a, b) => b[1] - a[1])
                .map(([cat, count]) => {
                  const pct = thuly ? (count / thuly) * 100 : 0
                  return (
                    <Box key={cat}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{CATEGORY_LABELS[cat] || cat}</Typography>
                        <Typography variant="body2" color="text.secondary">{count} vụ ({pct.toFixed(0)}%)</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 1 }} color="primary" />
                    </Box>
                  )
                })}
            </Box>
          </Paper>
        </Box>

        {/* Cột phải: Việc cần xử lý */}
        <Box sx={{ flex: '1 1 40%', minWidth: 280 }}>
          {/* Bước 6 – Phản hồi từ phiên hòa giải (kết nối ngược) */}
          <Paper sx={{ p: 2, mb: 2 }} elevation={0} variant="outlined">
            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Handshake fontSize="small" color="primary" /> Phản hồi từ phiên hòa giải
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Kết quả hòa giải trực tuyến phản hồi lên Chánh án để điều chỉnh phân công hoặc lịch xét xử nếu cần.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  borderLeft: 3,
                  borderLeftColor: 'warning.main',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
                onClick={() => navigate('/workspace/cases/case-3')}
              >
                <Typography variant="body2" fontWeight={600}>HN-GD-2026-003</Typography>
                <Typography variant="body2" color="text.secondary">Hòa giải không thành. Đề xuất đưa vụ án ra xét xử.</Typography>
                <Typography variant="caption" color="text.secondary">18/02/2026 · Phiên hòa giải</Typography>
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  borderLeft: 3,
                  borderLeftColor: 'success.main',
                }}
              >
                <Typography variant="body2" fontWeight={600}>HN-GD-2026-001</Typography>
                <Typography variant="body2" color="text.secondary">Đã hòa giải, chờ các bên xác nhận thỏa thuận.</Typography>
                <Typography variant="caption" color="text.secondary">15/02/2026</Typography>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }} elevation={0} variant="outlined">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>Đơn chờ quyết định thẩm quyền</Typography>
              {pendingJurisdiction.length > 0 && (
                <Chip icon={<Warning />} label={pendingJurisdiction.length} size="small" color="warning" />
              )}
            </Box>
            {pendingJurisdiction.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Không có đơn mới chờ quyết định.</Typography>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell><strong>Vụ án</strong></TableCell>
                        <TableCell><strong>Nguyên đơn/Bị đơn</strong></TableCell>
                        <TableCell align="right"><strong>Thao tác</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingJurisdiction.map((c) => (
                        <TableRow key={c.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{c.case_code}</TableCell>
                          <TableCell><Typography variant="body2">{c.plaintiff_name}/{c.defendant_name}</Typography></TableCell>
                          <TableCell align="right">
                            <Button size="small" variant="outlined" onClick={() => navigate('/workspace/preliminary-chief')}>Xử lý</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button size="small" fullWidth sx={{ mt: 1 }} onClick={() => navigate('/workspace/preliminary-chief')} endIcon={<ChevronRight />}>
                  Vào Quyết định thẩm quyền
                </Button>
              </>
            )}
          </Paper>

          <Paper sx={{ p: 2 }} elevation={0} variant="outlined">
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Hạn & phiên sắp tới</Typography>
            {upcomingDeadlines.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Không có hạn hoặc phiên trong thời gian tới.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {upcomingDeadlines.map((d) => (
                  <Box key={d.case_code + d.at} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{d.case_code}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {d.type} · {new Date(d.at).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                    <Button size="small" onClick={() => navigate(`/workspace/cases/${d.case_id}`)}>Chi tiết</Button>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  )
}
